import debounce from 'lodash/debounce'
import { fetchExperienceCategories, fetchExperienceListFacets } from 'actions/ExperienceActions'
import FilterPanelCheckboxGroup, { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelLoader from 'components/Common/FilterPanel/FilterPanelCheckboxLoader'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DualSliderInput from 'LuxKit/Slider/DualSliderInput'
import { arrayToObject, EmptyArray, groupBy, sortBy, sum, without } from 'lib/array/arrayUtils'
import React, { useCallback, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import Group from 'components/utils/Group'
import { EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID } from 'constants/experience'
import LuxPlusLabel from 'luxPlus/components/LuxPlusLabel'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'

interface CategoryItem {
  category: Omit<App.ExperienceItemCategory, 'name'> & { name: string | React.ReactNode }
  count: number
  items: Array<FilterPanelCheckItem>
  isLuxPlus?: boolean
}

interface Props {
  placeId: string
  facetState?: App.ExperienceListFacetsState[string]
  categories: Array<App.ExperienceItemCategory>
  isLuxPlusEnabled: boolean
}

function ExperienceSearchPageFilters({ categories = EmptyArray, facetState, placeId, isLuxPlusEnabled }: Props) {
  const dispatch = useAppDispatch()
  const {
    searchFilters,
    setPriceGteSearchFilter,
    setPriceLteSearchFilter,
    setSearchFilters,
  } = useExperiencesSearchFilters()

  useEffect(() => {
    dispatch(fetchExperienceListFacets(placeId))
    dispatch(fetchExperienceCategories())
  }, [dispatch, placeId])

  const onCategoryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const category = categories.find(cat => cat.id === parseInt(e.currentTarget.value))

    if (category) {
      const selectedCategories = searchFilters.categoryCodes ?? []
      let nextLuxPlusFeatures = searchFilters.luxPlusFeatures

      let nextCategories = [...selectedCategories]
      if (!category.parentId || selectedCategories.includes(category.parentId)) {
        // parentID selected - at the moment, all items for the parent are selected
        // in this instance, we toggle the parent and this category off but ensure all others remain
        const otherChildCategories = categories.filter(cat => cat.parentId === category.parentId && cat.id !== category.id)
        nextCategories = otherChildCategories.map(cat => cat.id)
      } else if (selectedCategories.includes(category.id)) {
        // toggling off a single category
        nextCategories = without(nextCategories, category.id)
      } else {
        // toggling on a single category
        if (category.id === EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID) {
          if (nextLuxPlusFeatures?.includes('Member prices')) {
            nextLuxPlusFeatures = undefined
          } else {
            nextLuxPlusFeatures = ['Member prices']
          }
        } else {
          nextCategories.push(category.id)
        }
      }

      setSearchFilters({
        ...searchFilters,
        categoryCodes: nextCategories,
        luxPlusFeatures: nextLuxPlusFeatures,
      })
    }
  }, [categories, searchFilters, setSearchFilters])

  const categoryItems = useMemo((): Array<CategoryItem> => {
    const childCategoriesByParent = groupBy(
      categories,
      cat => cat.parentId,
    )
    const facetsById = arrayToObject(facetState?.facets?.category ?? [], facet => facet.categoryId)
    const topLevelCategories = categories.filter(cat => cat.level === 'parent')
    const selectedCategories = searchFilters.categoryCodes ?? []

    return topLevelCategories.map((category) => {
      const isLuxPlus = category.id === EXP_CATEGORY_LUX_PLUS_ID
      const childCategories = sortBy(childCategoriesByParent.get(category.id) ?? [], cat => facetsById[cat.id]?.count ?? 0, 'desc')
      const total = sum(childCategories, cat => facetsById[cat.id]?.count ?? 0)

      return {
        category: {
          ...category,
          name: isLuxPlus ? <LuxPlusLabel type="only-logo" /> : category.name,
        },
        count: total,
        items: childCategories.map((category): FilterPanelCheckItem => {
          const count = facetsById[category.id]?.count ?? 0

          const checked = isLuxPlus ? !!searchFilters.luxPlusFeatures?.length : selectedCategories.includes(category.id) || selectedCategories.includes(category.parentId ?? -1)

          return {
            label: <Group direction="horizontal" horizontalAlign="space-between">
              {category.name}
              <span>({count})</span>
            </Group>,
            hidden: count === 0,
            value: category.id,
            checked,
          }
        }),
        isLuxPlus,
      }
    })
      .filter(val => val.count > 0 && (!val.isLuxPlus || isLuxPlusEnabled))
      // Make sure LuxPlus+ is always at the top
      .sort((a) => a.isLuxPlus ? -1 : 1)
  }, [categories, facetState?.facets?.category, searchFilters.categoryCodes, isLuxPlusEnabled, searchFilters.luxPlusFeatures])

  const slider = useMemo(() => {
    if (facetState?.facets?.price) {
      const { min: pmin, max: pmax } = facetState.facets.price
      const step = pmax <= 10000 ? 10 : 100
      const min = Math.floor(pmin / step) * step
      const max = Math.ceil(pmax / step) * step
      return { min, max, step }
    }
  }, [facetState])

  const onResetCategory = useCallback((item: CategoryItem) => {
    const selectedCategories = searchFilters.categoryCodes ?? []
    const nextCategories = without(selectedCategories, item.category.id, ...item.items.map(i => i.value as number))
    setSearchFilters({
      ...searchFilters,
      categoryCodes: nextCategories,
      luxPlusFeatures: undefined,
    })
  }, [searchFilters, setSearchFilters])

  const onPriceGteChange = useMemo(() => debounce<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setPriceGteSearchFilter(parseInt(event.target.value))
  }, 500), [setPriceGteSearchFilter])

  const onPriceLteChange = useMemo(() => debounce<React.ChangeEventHandler<HTMLInputElement>>((event) => {
    setPriceLteSearchFilter(parseInt(event.target.value))
  }, 500), [setPriceLteSearchFilter])

  return <VerticalSpacer gap={24}>
    {(!facetState || facetState.fetching) && <FilterPanelLoader type="checkbox" />}
    <FilterPanelFilterGroup
      title="Price"
    >
      { slider && <DualSliderInput
        format="currency"
        min={slider.min}
        max={slider.max}
        defaultStartValue={searchFilters.priceGte}
        defaultEndValue={searchFilters.priceLte}
        step={slider.step}
        onStartChange={onPriceGteChange}
        onEndChange={onPriceLteChange}
        variant="label"
      />}
    </FilterPanelFilterGroup>
    {categoryItems.map(item =>
      <FilterPanelFilterGroup
        key={item.category.id}
        title={item.category.name}
        canReset={item.items.some(item => item.checked)}
        onReset={() => onResetCategory(item)}
      >
        <FilterPanelCheckboxGroup
          name="categories"
          items={item.items}
          onChange={onCategoryChange}
          maxItems={5}
        />
      </FilterPanelFilterGroup>,
    )}
  </VerticalSpacer>
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    facetState: state.experience.experienceListFacets[ownProps.placeId!],
    categories: state.experience.experienceCategories,
    isLuxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(ExperienceSearchPageFilters)
